import type { MarkAreaComponentOption } from 'echarts'

import { formatDurationLong, formatHistoryDateTime } from '@/utils/formatDate'

import { GEOFENCE_OPACITY } from '../constants'
import type { LocationSample } from '../types'

export interface GeofenceInterval {
  name: string
  inside: boolean
  start: number // epoch ms
  end: number // epoch ms
}

export interface GeofenceLegendEntry {
  name: string
  color: string
}

export interface GeofenceResult {
  markArea: MarkAreaComponentOption
  intervals: GeofenceInterval[]
  legendEntries: GeofenceLegendEntry[]
}

/**
 * Walk location samples and build intervals for both inside-geofence (shaded)
 * and outside-geofence (transparent, tooltip-only) periods. `outsideBands: false`
 * leaves out the outside bands, for a chart whose cursor popover already says
 * where the asset was (they cover nearly the whole plot and would hide it).
 */
export function buildGeofenceMarkAreas(
  locations: LocationSample[],
  { outsideBands = true }: { outsideBands?: boolean } = {}
): GeofenceResult {
  if (!locations?.length) return { markArea: { data: [] }, intervals: [], legendEntries: [] }

  const intervals: GeofenceInterval[] = []
  let currentId: string | undefined
  let currentName = ''
  let intervalStart: string | undefined

  for (let i = 0; i < locations.length; i++) {
    const gf = locations[i].geofence
    const gfId = gf?.id != null ? String(gf.id) : ''
    if (gfId !== currentId) {
      if (intervalStart != null) {
        intervals.push({
          name: currentName,
          inside: !!currentId,
          start: new Date(intervalStart).getTime(),
          end: new Date(locations[i].time).getTime(),
        })
      }
      currentId = gfId
      currentName = gf?.name ?? 'Outside Geofence'
      intervalStart = locations[i].time
    }
  }

  if (intervalStart != null) {
    intervals.push({
      name: currentName,
      inside: !!currentId,
      start: new Date(intervalStart).getTime(),
      end: new Date(locations[locations.length - 1].time).getTime(),
    })
  }

  if (!intervals.length) return { markArea: { data: [] }, intervals: [], legendEntries: [] }

  const GEOFENCE_BLUE = '#93c5fd' // blue-300, slightly darker light blue for chart bands
  const LEGEND_BLUE = '#3b82f6' // darker blue for legend swatch
  const hasInside = intervals.some((iv) => iv.inside)

  const markAreaData = intervals
    .filter((iv) => outsideBands || iv.inside)
    .map((iv) => [
      {
        name: iv.name,
        xAxis: iv.start,
        endTime: iv.end,
        itemStyle: iv.inside ? { color: GEOFENCE_BLUE, opacity: GEOFENCE_OPACITY } : { color: 'transparent' },
        label: { show: false },
      },
      { xAxis: iv.end },
    ])

  return {
    markArea: {
      silent: false,
      tooltip: {
        show: true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter: (params: any) => {
          const name = params.name as string
          const startMs = params.data.xAxis as number
          const endMs = params.data.endTime as number
          const lines = [
            `<b>Geofence:</b> ${name}`,
            `Started: ${formatHistoryDateTime(startMs)}`,
            `Ended: ${formatHistoryDateTime(endMs)}`,
            `Duration: ${formatDurationLong(startMs, endMs)}`,
          ]
          return lines.join('<br/>')
        },
      },
      data: markAreaData as MarkAreaComponentOption['data'],
    },
    intervals,
    legendEntries: hasInside ? [{ name: 'In Geofence', color: LEGEND_BLUE }] : [],
  }
}
